import React, { FC, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import Warranty from 'components/lab-order/warranty/warranty.component';
import { ActionSet } from 'models/action-set';
import { ActionConstruction } from 'models/order-input/action-order';
import ConstructionForm from '../construction-form/construction-form.component';
import isEqual from 'lodash/isEqual';
import { useConstructions } from 'context/construction-context';
import TandvardCheckbox from '../tandvard-checkbox/tandvard-checkbox.component';
import { ActionOrderContext } from 'pages/order-flow/action-order/action-order-context';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type ModifyConstructionDialogProps = {
    constructionToModify?: ActionConstruction;
    open: boolean;
    onClose: () => void;
    actionSet: ActionSet;
    onModifyConstruction?: (construction: ActionConstruction) => void;
};
const ModifyConstructionDialog: FC<ModifyConstructionDialogProps> = ({
    constructionToModify,
    open,
    onClose,
    actionSet,
    onModifyConstruction,
}) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const { isConstructionValid, construction, setConstruction } = useConstructions();
    const { order } = useContext(ActionOrderContext);
    const currentOrg = currentOrgVar();

    const isTioTandvardPricingEnabled = customerSettings.getCustomerSetting(
        order?.customer?.customerSettings || currentOrg?.customerSettings || [],
        CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING,
    );
    useEffect(() => {
        if (constructionToModify) {
            setConstruction(constructionToModify);
        }
    }, [constructionToModify]);

    const handleClose = () => {
        onClose();
    };

    const handleConstructionChanged = (construction: ActionConstruction, isValid: boolean) => {
        if (!isEqual(construction, constructionToModify)) {
            setConstruction((prev) => ({
                ...prev,
                ...construction,
                warranty: prev.warranty || false,
                ...(prev.warrantyType ? { warrantyType: prev.warrantyType } : {}),
            }));
        }
    };

    const handleHandleWarrantyChange = (warranty: boolean, warrantyType: string) => {
        setConstruction((prev) => ({ ...prev, warranty, warrantyType }));
    };

    const handleHandleTandvardChange = (isTioTandvard: boolean) => {
        setConstruction((prev) => ({ ...prev, isTioTandvard }));
    };

    const handleSaveButtonClicked = () => {
        if (onModifyConstruction) {
            onModifyConstruction(construction);
        }
        handleClose();
    };

    return (
        <DHDialog
            open={open}
            onClose={handleClose}
            disableBackdropClick
            maxWidth="xl"
            fullWidth
            title={translate('orders:MODIFY_CONSTRUCTION')}
            actions={
                <>
                    {isTioTandvardPricingEnabled && (
                        <TandvardCheckbox
                            handleTandvardCheckbox={handleHandleTandvardChange}
                            isTioTandvard={construction.isTioTandvard || false}
                        />
                    )}
                    <Warranty
                        handleWarranty={handleHandleWarrantyChange}
                        warranty={construction.warranty || false}
                        warrantyType={construction.warrantyType || ''}
                    />
                    <Button variant="contained" disabled={!isConstructionValid} onClick={handleSaveButtonClicked}>
                        {translate('common:SAVE')}
                    </Button>
                </>
            }
        >
            <Box>
                <ConstructionForm
                    construction={construction}
                    actionSet={actionSet}
                    onConstructionChanged={handleConstructionChanged}
                />
            </Box>
        </DHDialog>
    );
};

export default ModifyConstructionDialog;
